/**
 * Webinar and tutorial links shown around the product.
 * An empty href still renders the label, without a destination.
 */

import { CalendarBlankIcon, ChalkboardTeacherIcon, EnvelopeSimpleIcon, type Icon } from '@phosphor-icons/react';

export interface HelpLinkItem {
  id: string;
  label: string;
  /** Internal path, absolute URL, or mailto. Empty until the destination is confirmed. */
  href: string;
  icon: Icon;
  onClick?: () => void;
}

export interface CatalogueTutorial {
  slug: string;
  title: string;
  section: string;
  position: number;
}

/** Live training and drop-in sessions. Each event on the calendar can be added to Outlook. */
export const TRAINING_CALENDAR_URL = 'https://www.addevent.com/calendar/rc44g1g7qycf';

/** Published tutorials for using Groups, on the tutorials index. */
export const GROUPS_TUTORIALS_HREF = '/tutorials?section=groups';

/** Published tutorials for PAM, on the tutorials index. */
export const PAM_TUTORIALS_HREF = '/tutorials?section=pam';

/** Walkthrough for turning a meeting into minutes and actions. */
export const FINALISE_MEETING_TUTORIAL_HREF = '/tutorials/turn-meeting-transcripts-into-auditable-minutes-and-actions';

const SUPPORT_EMAIL_HREF = 'mailto:support@complimind.co.uk?subject=Help%20with%20CompliMind';

export const introToComplimindLink: HelpLinkItem = {
  id: 'intro-to-complimind',
  label: 'Webinar: Intro to CompliMind: new user onboarding and refresher training',
  href: TRAINING_CALENDAR_URL,
  icon: CalendarBlankIcon
};

export const safetyGroupSetupWalkthroughLink: HelpLinkItem = {
  id: 'safety-group-setup-walkthrough',
  label: 'Webinar: Set up a Safety Group on a live walk-through',
  href: TRAINING_CALENDAR_URL,
  icon: CalendarBlankIcon
};

export const administerSafetyGroupLink: HelpLinkItem = {
  id: 'administer-safety-group',
  label: 'Webinar: How to administer a Safety Group',
  href: TRAINING_CALENDAR_URL,
  icon: CalendarBlankIcon
};

export const groupsTutorialsLink: HelpLinkItem = {
  id: 'groups-tutorials',
  label: 'Tutorial: Watch in app tutorials to learn how to use groups',
  href: GROUPS_TUTORIALS_HREF,
  icon: ChalkboardTeacherIcon
};

export const howToUseGroupsLink: HelpLinkItem = {
  id: 'how-to-use-groups',
  label: 'Tutorial: Watch in app tutorials to learn how to use groups',
  href: GROUPS_TUTORIALS_HREF,
  icon: ChalkboardTeacherIcon
};

export const finaliseMeetingLink: HelpLinkItem = {
  id: 'finalise-meeting',
  label: 'Tutorial: Learn how to finalise a meeting',
  href: FINALISE_MEETING_TUTORIAL_HREF,
  icon: ChalkboardTeacherIcon
};

export const furtherHelpLink: HelpLinkItem = {
  id: 'further-help',
  label: 'Need further help? Send us an email',
  href: SUPPORT_EMAIL_HREF,
  icon: EnvelopeSimpleIcon
};

/** Shown on the groups directory once the get-started card is dismissed. */
export const groupsDirectoryHelpLinks: HelpLinkItem[] = [safetyGroupSetupWalkthroughLink, howToUseGroupsLink];

/** Shown on an individual group page. */
export const groupHubHelpLinks: HelpLinkItem[] = [
  introToComplimindLink,
  administerSafetyGroupLink,
  groupsTutorialsLink
];

/** Shown at the top of the tutorials index, with email help last. */
export const tutorialsPageHelpLinks: HelpLinkItem[] = [
  introToComplimindLink,
  safetyGroupSetupWalkthroughLink,
  administerSafetyGroupLink,
  furtherHelpLink
];

const DASHBOARD_TUTORIALS: { id: string; label: string; match: RegExp }[] = [
  {
    id: 'build-and-populate-group-dashboards-in-complimind',
    label: 'Tutorial: Build and Populate Group Dashboards in CompliMind',
    match: /build and populate group dashboards/i
  },
  {
    id: 'aggregate-safety-dashboards-into-leadership-assurance-view',
    label: 'Tutorial: Aggregate Safety Dashboards into Leadership Assurance View',
    match: /leadership assurance/i
  },
  {
    id: 'prioritize-compliance-actions-from-dashboard-insights',
    label: 'Tutorial: Prioritise Compliance Actions from Dashboard Insights',
    match: /priori[sz]e compliance actions/i
  }
];

/** Dashboard tutorials, using the published slug when the catalogue has loaded. */
export function dashboardTutorialLinks(tutorials: CatalogueTutorial[]): HelpLinkItem[] {
  return DASHBOARD_TUTORIALS.map((item) => {
    const published = tutorials.find((tutorial) => item.match.test(tutorial.title));
    const slug = published?.slug ?? item.id;
    return {
      id: slug,
      label: item.label,
      href: `/tutorials/${slug}`,
      icon: ChalkboardTeacherIcon
    };
  });
}
